import React, { useState, useEffect, useRef, useCallback } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";
import "./Toast.css";

const Toast = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const remainingRef = useRef(toast.duration || 0);
  const startTimeRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  }, []);

  const handleRemove = useCallback(() => {
    setIsRemoving(true);
    setTimeout(() => onRemove(toast.id), 200);
  }, [onRemove, toast.id]);

  const startTimer = useCallback(() => {
    if (!toast.pauseOnHover) return;
    if (toast.persistent || !toast.duration || toast.duration <= 0) return;
    startTimeRef.current = Date.now();
    clearTimer();
    setProgress((remainingRef.current / toast.duration) * 100);
    timerRef.current = setTimeout(() => {
      handleRemove();
    }, remainingRef.current);
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const nextRemaining = Math.max(0, remainingRef.current - elapsed);
      setProgress((nextRemaining / toast.duration) * 100);
    }, 50);
  }, [
    toast.pauseOnHover,
    toast.persistent,
    toast.duration,
    clearTimer,
    handleRemove,
  ]);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) return;
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    setProgress(
      toast.duration > 0 ? (remainingRef.current / toast.duration) * 100 : 0
    );
    clearTimer();
  }, [clearTimer, toast.duration]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      remainingRef.current = toast.duration || 0;
      setProgress(100);
      startTimer();
    }, 50);
    return () => {
      clearTimeout(timer);
      clearTimer();
    };
  }, [startTimer, clearTimer, toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return <CheckCircle size={16} />;
      case "error":
        return <XCircle size={16} />;
      case "warning":
        return <AlertTriangle size={16} />;
      case "info":
      default:
        return <Info size={16} />;
    }
  };

  return (
    <div
      className={`toast toast--${toast.type} ${
        isVisible ? "toast--visible" : ""
      } ${isRemoving ? "toast--removing" : ""}`}
      role="alert"
      aria-live="polite"
      onMouseEnter={() => {
        if (toast.pauseOnHover) pauseTimer();
      }}
      onMouseLeave={() => {
        if (toast.pauseOnHover) startTimer();
      }}
    >
      <div className="toast__icon">{getIcon()}</div>
      <div className="toast__content">
        <p className="toast__message">
          {typeof toast.message === "string"
            ? toast.message
            : toast.message?.response?.data?.message ||
              toast.message?.message ||
              String(toast.message ?? "Erro desconhecido")}
        </p>
      </div>
      {!toast.persistent && (
        <button
          className="toast__close"
          onClick={handleRemove}
          aria-label="Fechar notificação"
        >
          <X size={14} />
        </button>
      )}
      {!toast.persistent && toast.duration > 0 && (
        <div className="toast__progress" aria-hidden="true">
          <div
            className="toast__progress-bar"
            style={{ transform: `scaleX(${Math.max(progress, 0) / 100})` }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
